import { QUESTIONS_PER_PAGE } from '@shared/shared-constants/community-profile-fields';

import type {
  BuiltInFieldKey,
  CommunityProfileField,
  ProfileFieldAnswer,
  ProfileFieldErrors,
  ProfileFieldValue,
} from '~/interfaces/community';
import { ProfileFieldValueStatus } from '~/interfaces/community';

export { onboardingQuestions, questionPages } from '@shared/shared-constants/community-profile-fields';

const DATETIME_LOCAL_LENGTH = 16;

const liveOptionValues = (field: CommunityProfileField): string[] =>
  field.custom_field?.options.map((option) => option.value) ?? [];

const isUnlistableList = (field: CommunityProfileField): boolean =>
  field.custom_field?.kind === 'list' && liveOptionValues(field).length === 0;

export type BuiltInFieldConfig = {
  visible: boolean;
  visibility: CommunityProfileField['visibility'];
  required: boolean;
  /** Publisher wording, or null when the untouched default should stay translatable. */
  display: string | null;
};

// Only the surfaces where a member fills a built-in read this, so the question wins over the profile
// label. The server seeds display as the built-in's own name, so any other display is a rename.
export const builtInField = (fields: CommunityProfileField[], key: BuiltInFieldKey): BuiltInFieldConfig => {
  const field = fields.find((entry) => entry.system_key === key);
  const visible = field?.visibility !== 'hidden';
  const renamed = field && field.display.toLowerCase() !== key ? field.display : null;
  return {
    visible,
    visibility: field?.visibility ?? 'public',
    required: visible && field?.required === true,
    display: field?.asked_as?.trim() || renamed,
  };
};

export const visibilityHintKey = (visibility: CommunityProfileField['visibility']): string =>
  visibility === 'private' ? 'community.profile_fields.private_hint' : 'community.profile_fields.public_hint';

export const builtInFieldValues = (values: Partial<Record<BuiltInFieldKey, string>>): Record<string, string> =>
  Object.fromEntries(Object.entries(values).filter((entry): entry is [string, string] => entry[1] !== undefined));

export const editableProfileFields = (fields: CommunityProfileField[]): CommunityProfileField[] =>
  fields.filter((field) => field.visibility !== 'hidden' && field.custom_field != null && !isUnlistableList(field));

export const requiredSetupFields = (fields: CommunityProfileField[]): CommunityProfileField[] =>
  editableProfileFields(fields).filter(
    (field) => field.required && field.value_status !== ProfileFieldValueStatus.Valid,
  );

export const paginateProfileFields = (
  fields: CommunityProfileField[],
  pageSize: number = QUESTIONS_PER_PAGE,
): CommunityProfileField[][] => {
  if (fields.length === 0 || pageSize < 1) return [];

  const pages: CommunityProfileField[][] = [];
  for (let index = 0; index < fields.length; index += pageSize) {
    pages.push(fields.slice(index, index + pageSize));
  }
  return pages;
};

export const requiredSetupPages = (
  fields: CommunityProfileField[],
  pageSize: number = QUESTIONS_PER_PAGE,
): CommunityProfileField[][] => paginateProfileFields(requiredSetupFields(fields), pageSize);

const unansweredValue = (field: CommunityProfileField): ProfileFieldValue => {
  if (field.custom_field?.kind === 'list') return [];
  if (field.custom_field?.kind === 'string') return '';
  return null;
};

export const serializeAnswer = (field: CommunityProfileField, value: ProfileFieldValue): ProfileFieldValue => {
  const kind = field.custom_field?.kind;
  if (kind === 'list') {
    const selected = Array.isArray(value) ? value.filter((entry) => entry.trim().length > 0) : [];
    const live = new Set(liveOptionValues(field));
    return selected.filter((entry) => live.has(entry));
  }
  if (kind === 'string') {
    return typeof value === 'string' ? value : '';
  }
  if (kind === 'date' || kind === 'datetime') {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (kind === 'integer' || kind === 'number') {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : value;
    }
    return null;
  }
  if (kind === 'boolean') {
    return value === true || value === false ? value : null;
  }
  return value ?? null;
};

export const initialEditableAnswers = (fields: CommunityProfileField[]): Record<string, ProfileFieldValue> =>
  Object.fromEntries(
    editableProfileFields(fields).map((field) => [
      field.id,
      serializeAnswer(field, field.value ?? unansweredValue(field)),
    ]),
  );

export const displayInputValue = (field: CommunityProfileField, value: ProfileFieldValue): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const asString = String(value);
  if (field.custom_field?.kind === 'datetime') return asString.slice(0, DATETIME_LOCAL_LENGTH);
  return asString;
};

export const isUnanswered = (field: CommunityProfileField, value: ProfileFieldValue): boolean => {
  const serialized = serializeAnswer(field, value);
  if (serialized == null) return true;
  if (field.custom_field?.kind === 'list') return Array.isArray(serialized) && serialized.length === 0;
  if (field.custom_field?.kind === 'string') return typeof serialized === 'string' && serialized.trim() === '';
  return false;
};

export const validatePageAnswers = (
  fields: CommunityProfileField[],
  answers: Record<string, ProfileFieldValue>,
  requiredMessage: string,
): ProfileFieldErrors => {
  const errors: ProfileFieldErrors = {};
  for (const field of fields) {
    if (!field.required) continue;
    if (isUnanswered(field, answers[field.id] ?? unansweredValue(field))) {
      errors[field.id] = [requiredMessage];
    }
  }
  return errors;
};

export const pageAnswersPayload = (
  fields: CommunityProfileField[],
  answers: Record<string, ProfileFieldValue>,
): ProfileFieldAnswer[] =>
  fields.map((field) => ({
    id: field.id,
    value: serializeAnswer(field, answers[field.id] ?? unansweredValue(field)),
  }));
